import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl/dist/mapbox-gl-csp";
import "mapbox-gl/dist/mapbox-gl.css";
import { useTheme } from "@/lib/theme";

// Use the CSP build + an explicit worker. mapbox-gl's default inlined worker
// gets corrupted by the production bundler/minifier (dev/preview is unminified,
// so it worked there), which threw "Cannot read properties of undefined
// (reading 'composite')" and left the map blank on getarideorlando.com.
import MapboxWorker from "worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker";
mapboxgl.workerClass = MapboxWorker;
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const ORLANDO = [-81.3789, 28.5384];

function haversineM(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

function fmtDist(m) {
  const ft = m * 3.28084;
  if (ft < 1000) return `${Math.max(0, Math.round(ft / 10) * 10)} ft`;
  return `${(m / 1609.34).toFixed(1)} mi`;
}

function maneuverArrow(type, modifier) {
  if (type === "arrive") return "🏁";
  if (modifier?.includes("left")) return "←";
  if (modifier?.includes("right")) return "→";
  if (modifier === "uturn") return "↩";
  return "↑";
}

function makeEl(html, size = 30) {
  const el = document.createElement("div");
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.innerHTML = html;
  return el;
}

const pinSvg = (color) =>
  `<svg viewBox="0 0 24 24" width="100%" height="100%" fill="${color}" stroke="white" stroke-width="1.5" style="filter:drop-shadow(0 2px 4px rgba(0,0,0,.35))"><path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z"/><circle cx="12" cy="9" r="2.6" fill="white" stroke="none"/></svg>`;

const dotSvg = (color) =>
  `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.4)"></div>`;

// Pulsating rider-location marker (like the mobile app) so the driver sees the live pickup.
function makePulseEl(color = "#9333ea") {
  const el = document.createElement("div");
  el.style.cssText = "position:relative;width:26px;height:26px;pointer-events:none;";
  const ring = document.createElement("div");
  ring.style.cssText = `position:absolute;inset:0;border-radius:50%;background:${color};`;
  const dot = document.createElement("div");
  dot.style.cssText = `position:absolute;inset:7px;border-radius:50%;background:${color};border:2.5px solid white;box-shadow:0 1px 5px rgba(0,0,0,.45);`;
  el.appendChild(ring);
  el.appendChild(dot);
  try {
    ring.animate(
      [{ transform: "scale(0.9)", opacity: 0.55 }, { transform: "scale(2.8)", opacity: 0 }],
      { duration: 1600, iterations: Infinity, easing: "ease-out" }
    );
  } catch { /* noop */ }
  return el;
}

// Branded Getaride car marker (top-down PNG). Artwork points north-east (~45°),
// so rotation is offset by -45° to align the nose with the heading.
const CAR_ICON = "/getaride-car.png";
const CAR_ASPECT = 103 / 150;
function makeCarEl(px = 46) {
  const el = document.createElement("div");
  const h = Math.round(px * CAR_ASPECT);
  el.style.cssText = `width:${px}px;height:${h}px;pointer-events:none;`;
  const img = document.createElement("img");
  img.src = CAR_ICON;
  img.style.cssText = "width:100%;height:100%;object-fit:contain;filter:drop-shadow(0 3px 6px rgba(0,0,0,.35));";
  img.draggable = false;
  el.appendChild(img);
  return el;
}
// Car marker with a pulsating ring behind it (live driver location for the rider).
function makeCarPulseEl(px = 46) {
  const el = document.createElement("div");
  const h = Math.round(px * CAR_ASPECT);
  el.style.cssText = `position:relative;width:${px}px;height:${px}px;pointer-events:none;`;
  const ring = document.createElement("div");
  ring.style.cssText = `position:absolute;left:50%;top:50%;width:${px}px;height:${px}px;margin-left:${-px / 2}px;margin-top:${-px / 2}px;border-radius:50%;background:rgba(147,51,234,0.35);`;
  const img = document.createElement("img");
  img.src = CAR_ICON;
  img.style.cssText = `position:absolute;left:50%;top:50%;width:${px}px;height:${h}px;margin-left:${-px / 2}px;margin-top:${-h / 2}px;object-fit:contain;filter:drop-shadow(0 3px 6px rgba(0,0,0,.4));`;
  img.draggable = false;
  el.appendChild(ring);
  el.appendChild(img);
  try {
    ring.animate(
      [{ transform: "scale(0.55)", opacity: 0.6 }, { transform: "scale(2)", opacity: 0 }],
      { duration: 1600, iterations: Infinity, easing: "ease-out" }
    );
  } catch { /* noop */ }
  return el;
}
function bearingDeg(a, b) {
  const toR = Math.PI / 180;
  const y = Math.sin((b.lng - a.lng) * toR) * Math.cos(b.lat * toR);
  const x = Math.cos(a.lat * toR) * Math.sin(b.lat * toR) - Math.sin(a.lat * toR) * Math.cos(b.lat * toR) * Math.cos((b.lng - a.lng) * toR);
  return (Math.atan2(y, x) * 180) / Math.PI;
}

// Snap a point onto a [lng,lat][] polyline: nearest vertex + heading toward the next vertex.
function snapToLine(p, coords) {
  if (!coords || coords.length < 2) return null;
  let bi = 0, bd = Infinity;
  for (let i = 0; i < coords.length; i++) {
    const dLat = coords[i][1] - p.lat, dLng = coords[i][0] - p.lng;
    const d = dLat * dLat + dLng * dLng;
    if (d < bd) { bd = d; bi = i; }
  }
  const cur = { lat: coords[bi][1], lng: coords[bi][0] };
  const nxt = { lat: coords[Math.min(bi + 1, coords.length - 1)][1], lng: coords[Math.min(bi + 1, coords.length - 1)][0] };
  return { lat: cur.lat, lng: cur.lng, bearing: bearingDeg(cur, nxt), idx: bi };
}

export default function MapView({
  pickup,
  destination,
  stops = [],
  driver,
  requestMarkers = [],
  centerOn = null,
  navFrom = null,
  navTo = null,
  follow = false,
  recenterKey = 0,
  pulsePickup = false,
  onRouteInfo,
  onNavStep,
  interactive = false,
  onPickupChange,
  showRoute = true,
  onRoute,
  forceDark = false,
  ambientCars = 0,
  driverPulse = false,
  trackTo = null,
  className = "",
}) {
  const { dark: themeDark } = useTheme();
  const dark = forceDark || themeDark;
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const readyRef = useRef(false);
  const navStepsRef = useRef([]);
  const navLegKeyRef = useRef(null);
  const navTotalRef = useRef({ dist: 0, dur: 0 });
  const navToKindRef = useRef(null);
  const navGeomRef = useRef(null);
  const [mapErr, setMapErr] = useState(!mapboxgl.accessToken ? "missing" : null);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    if (!mapboxgl.accessToken) { setMapErr("missing"); return; }
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: dark ? "mapbox://styles/mapbox/dark-v11" : "mapbox://styles/mapbox/streets-v12",
      center: pickup ? [pickup.lng, pickup.lat] : centerOn ? [centerOn.lng, centerOn.lat] : ORLANDO,
      zoom: 11,
      attributionControl: false,
      projection: "mercator",
    });
    mapRef.current = map;
    map.on("load", () => {
      readyRef.current = true;
      setMapErr(null);
      updateMap();
      // Real-device safeguard: mobile Safari often lays the container out AFTER
      // the map inits (tabs/flex), leaving a 0-size canvas that paints blank
      // even though tiles load fine. Force a couple of resizes post-load.
      map.resize();
      setTimeout(() => { try { map.resize(); } catch (e) { /* noop */ } }, 400);
    });
    map.on("error", (e) => {
      if (readyRef.current) return; // ignore transient errors once the map has loaded
      const msg = String(e?.error?.message || e?.error?.status || "");
      if (/401|403|Unauthorized|Forbidden|access token/i.test(msg)) setMapErr("auth");
    });
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => { try { map.resize(); } catch (e) { /* noop */ } });
      ro.observe(containerRef.current);
    }
    return () => {
      if (ro) ro.disconnect();
      map.remove();
      mapRef.current = null;
      readyRef.current = false;
    };
  }, []);

  // Update map style on theme change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    map.setStyle(dark ? "mapbox://styles/mapbox/dark-v11" : "mapbox://styles/mapbox/streets-v12");
    map.once("styledata", () => setTimeout(updateMap, 60));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dark]);

  useEffect(() => {
    if (readyRef.current) updateMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pickup?.lat, pickup?.lng, destination?.lat, destination?.lng,
    JSON.stringify(stops), driver?.lat, driver?.lng,
    JSON.stringify(requestMarkers), centerOn?.lat, centerOn?.lng,
    navFrom?.lat, navFrom?.lng, navTo?.lat, navTo?.lng, follow, driverPulse, trackTo?.lat, trackTo?.lng,
  ]);

  // Recenter the follow camera on demand.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current || !navFrom) return;
    map.easeTo({ center: [navFrom.lng, navFrom.lat], zoom: 15.5, duration: 700 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recenterKey]);

  // Ambient simulated drivers that follow real roads (Mapbox Directions).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ambientCars) return;
    const A = { cars: [], raf: null, stop: false };
    const token = mapboxgl.accessToken;
    const speed = 0.00000012; // ground speed in degrees per ms (~45 km/h)
    // Pick a random point inside the current viewport (inset from edges) so cars stay on-screen.
    const pickInView = () => {
      const b = map.getBounds();
      const sw = b.getSouthWest(), ne = b.getNorthEast();
      return {
        lat: sw.lat + (ne.lat - sw.lat) * (0.1 + Math.random() * 0.8),
        lng: sw.lng + (ne.lng - sw.lng) * (0.1 + Math.random() * 0.8),
      };
    };
    const fetchRoute = async (from, to) => {
      try {
        const r = await fetch(`https://api.mapbox.com/directions/v5/mapbox/driving/${from.lng},${from.lat};${to.lng},${to.lat}?geometries=geojson&overview=full&access_token=${token}`);
        const j = await r.json();
        return j.routes?.[0]?.geometry?.coordinates || null;
      } catch { return null; }
    };
    const newRoute = async (car) => {
      car.loading = true;
      const coords = await fetchRoute(car.from, pickInView());
      if (A.stop) return;
      if (coords && coords.length >= 2) {
        car.coords = coords; car.seg = 0; car.t = 0;
        car.from = { lat: coords[coords.length - 1][1], lng: coords[coords.length - 1][0] };
        // snap the car onto the road at the route's first point, then reveal it
        car.marker.setLngLat([coords[0][0], coords[0][1]]);
        car.marker.getElement().style.opacity = "1";
      } else { car.from = pickInView(); }
      car.loading = false;
    };
    const start = () => {
      if (A.stop) return;
      for (let i = 0; i < ambientCars; i++) {
        const from = pickInView();
        const el = makeCarEl(30);
        el.style.opacity = "0";
        el.style.transition = "opacity 500ms ease";
        const marker = new mapboxgl.Marker({ element: el, anchor: "center", rotationAlignment: "map" }).setLngLat([from.lng, from.lat]).addTo(map);
        const car = { marker, from, coords: null, seg: 0, t: 0, loading: false };
        A.cars.push(car);
        newRoute(car);
      }
      let last = performance.now();
      const tick = (now) => {
        if (A.stop) return;
        const dt = Math.min(64, now - last); last = now;
        for (const car of A.cars) {
          if (!car.coords) continue;
          if (car.seg >= car.coords.length - 1) { if (!car.loading) newRoute(car); continue; }
          const a = car.coords[car.seg], b = car.coords[car.seg + 1];
          const segLen = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1e-9;
          car.t += (speed * dt) / segLen;
          while (car.t >= 1 && car.seg < car.coords.length - 1) { car.t -= 1; car.seg++; }
          if (car.seg >= car.coords.length - 1) { if (!car.loading) newRoute(car); continue; }
          const a2 = car.coords[car.seg], b2 = car.coords[car.seg + 1];
          car.marker.setLngLat([a2[0] + (b2[0] - a2[0]) * car.t, a2[1] + (b2[1] - a2[1]) * car.t]);
          try { car.marker.setRotation(bearingDeg({ lat: a2[1], lng: a2[0] }, { lat: b2[1], lng: b2[0] }) - 45); } catch { /* noop */ }
        }
        A.raf = requestAnimationFrame(tick);
      };
      A.raf = requestAnimationFrame(tick);
    };
    let started = false;
    const go = () => { if (!started && !A.stop) { started = true; start(); } };
    map.once("idle", go);
    const fb = setTimeout(go, 1600);
    return () => {
      A.stop = true;
      clearTimeout(fb);
      if (A.raf) cancelAnimationFrame(A.raf);
      A.cars.forEach((c) => c.marker.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambientCars, pickup?.lat, pickup?.lng, centerOn?.lat, centerOn?.lng]);

  function setMarker(key, coord, el, anchor = "bottom") {
    const map = mapRef.current;
    if (!coord) {
      if (markersRef.current[key]) {
        markersRef.current[key].remove();
        delete markersRef.current[key];
      }
      return;
    }
    if (markersRef.current[key]) {
      markersRef.current[key].setLngLat([coord.lng, coord.lat]);
    } else {
      const m = new mapboxgl.Marker({ element: el, anchor })
        .setLngLat([coord.lng, coord.lat])
        .addTo(map);
      markersRef.current[key] = m;
    }
  }

  function carMarker(key, coord, bearing, pulse = false) {
    const map = mapRef.current;
    if (!coord) {
      if (markersRef.current[key]) { markersRef.current[key].remove(); delete markersRef.current[key]; }
      return;
    }
    if (!markersRef.current[key]) {
      markersRef.current[key] = new mapboxgl.Marker({ element: pulse ? makeCarPulseEl(46) : makeCarEl(46), anchor: "center", rotationAlignment: "map" })
        .setLngLat([coord.lng, coord.lat]).addTo(map);
    } else {
      markersRef.current[key].setLngLat([coord.lng, coord.lat]);
    }
    if (bearing != null) { try { markersRef.current[key].setRotation(bearing - 45); } catch { /* noop */ } }
  }

  async function drawRoute(points) {
    const map = mapRef.current;
    if (!map) return;
    const coordsStr = points.map((p) => `${p.lng},${p.lat}`).join(";");
    let geo = { type: "LineString", coordinates: points.map((p) => [p.lng, p.lat]) };
    try {
      const r = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${coordsStr}?geometries=geojson&overview=full&access_token=${mapboxgl.accessToken}`
      );
      const j = await r.json();
      if (j.routes?.[0]?.geometry) {
        geo = j.routes[0].geometry;
        if (onRoute) onRoute({ miles: j.routes[0].distance / 1609.34, minutes: Math.round(j.routes[0].duration / 60) });
      }
    } catch { /* noop */ }
    const data = { type: "Feature", geometry: geo };
    if (map.getSource("route")) map.getSource("route").setData(data);
    else map.addSource("route", { type: "geojson", data });
    ensureRouteLayers(map);
  }

  // Thick, app-style route line with a darker casing underneath.
  function ensureRouteLayers(map) {
    if (!map.getSource("route")) return;
    if (!map.getLayer("route-casing")) {
      map.addLayer({
        id: "route-casing", type: "line", source: "route",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#4c1d95", "line-width": 12, "line-opacity": 0.4 },
      });
    }
    if (!map.getLayer("route-line")) {
      map.addLayer({
        id: "route-line", type: "line", source: "route",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#9333ea", "line-width": 8, "line-opacity": 0.95 },
      });
    }
  }

  async function buildNavRoute(from, to) {
    const map = mapRef.current;
    if (!map) return;
    const coordsStr = `${from.lng},${from.lat};${to.lng},${to.lat}`;
    let geo = { type: "LineString", coordinates: [[from.lng, from.lat], [to.lng, to.lat]] };
    navStepsRef.current = [];
    try {
      const r = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${coordsStr}?geometries=geojson&overview=full&steps=true&access_token=${mapboxgl.accessToken}`
      );
      const j = await r.json();
      const route = j.routes?.[0];
      if (route) {
        geo = route.geometry;
        navTotalRef.current = { dist: route.distance, dur: route.duration };
        navStepsRef.current = (route.legs?.[0]?.steps || []).map((s) => ({
          loc: { lat: s.maneuver.location[1], lng: s.maneuver.location[0] },
          instruction: s.maneuver.instruction,
          type: s.maneuver.type,
          modifier: s.maneuver.modifier,
        }));
      }
    } catch { /* noop */ }
    const data = { type: "Feature", geometry: geo };
    navGeomRef.current = Array.isArray(geo.coordinates) ? geo.coordinates : null;
    if (map.getSource("route")) map.getSource("route").setData(data);
    else map.addSource("route", { type: "geojson", data });
    ensureRouteLayers(map);
    emitNavStep(from);
  }

  function emitNavStep(from) {
    const steps = navStepsRef.current;
    if (!steps.length) { onNavStep?.(null); return; }
    // Next maneuver = closest step ahead (skip the depart step at index 0 if a turn follows).
    let best = steps[steps.length - 1];
    let bestD = Infinity;
    for (let i = 1; i < steps.length; i++) {
      const d = haversineM(from, steps[i].loc);
      if (d < bestD) { bestD = d; best = steps[i]; }
    }
    const remaining = haversineM(from, navTo);
    onNavStep?.({
      instruction: best.instruction,
      distanceText: fmtDist(bestD === Infinity ? 0 : bestD),
      distanceM: bestD === Infinity ? null : bestD,
      arrow: maneuverArrow(best.type, best.modifier),
      remainingM: remaining,
    });
    const dur = Math.max(1, Math.round((remaining / Math.max(1, navTotalRef.current.dist)) * navTotalRef.current.dur / 60));
    const eta = new Date(Date.now() + dur * 60000);
    onRouteInfo?.({
      distanceText: fmtDist(remaining),
      durationText: `${dur} min`,
      durationMin: dur,
      arrivalText: eta.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
      remainingM: remaining,
    });
  }

  function updateMap() {
    const map = mapRef.current;
    if (!map) return;

    // ---- Turn-by-turn navigation mode ----
    if (navFrom && navTo) {
      ["pickup", "destination", "driver"].forEach((k) => setMarker(k, null));
      Object.keys(markersRef.current).forEach((k) => {
        if (k.startsWith("stop-") || k.startsWith("req-")) { markersRef.current[k].remove(); delete markersRef.current[k]; }
      });
      // Rider location = pulsating marker when heading to pickup; a flag pin for the destination leg.
      const navKind = pulsePickup ? "pulse" : "flag";
      if (navToKindRef.current !== navKind && markersRef.current["navto"]) {
        markersRef.current["navto"].remove();
        delete markersRef.current["navto"];
      }
      navToKindRef.current = navKind;
      if (navKind === "pulse") setMarker("navto", navTo, makePulseEl("#9333ea"), "center");
      else setMarker("navto", navTo, makeEl(pinSvg("#9333ea"), 34));
      const legKey = `${navTo.lat.toFixed(5)},${navTo.lng.toFixed(5)}`;
      if (navLegKeyRef.current !== legKey) {
        navLegKeyRef.current = legKey;
        navGeomRef.current = null;
        buildNavRoute(navFrom, navTo);
      } else {
        emitNavStep(navFrom);
      }
      // Snap the car onto the route line and trim the drawn line to the REMAINING portion.
      const snap = snapToLine(navFrom, navGeomRef.current);
      const carPos = snap ? { lat: snap.lat, lng: snap.lng } : navFrom;
      const carBear = snap ? snap.bearing : bearingDeg(navFrom, navTo);
      carMarker("navdriver", carPos, carBear);
      if (snap && navGeomRef.current && map.getSource("route")) {
        const remaining = navGeomRef.current.slice(snap.idx);
        if (remaining.length >= 2) {
          map.getSource("route").setData({ type: "Feature", geometry: { type: "LineString", coordinates: remaining } });
        }
      }
      // North-up, car-followed camera.
      if (follow) map.easeTo({ center: [carPos.lng, carPos.lat], zoom: 15.5, bearing: 0, pitch: 0, duration: 700 });
      return;
    }
    // leaving nav mode: clear nav markers + remembered leg
    if (navLegKeyRef.current) {
      navLegKeyRef.current = null;
      navStepsRef.current = [];
      navToKindRef.current = null;
      navGeomRef.current = null;
      ["navto", "navdriver"].forEach((k) => setMarker(k, null));
    }

    // pickup marker (draggable when interactive)
    if (pickup) {
      const key = "pickup";
      if (markersRef.current[key]) markersRef.current[key].setLngLat([pickup.lng, pickup.lat]);
      else {
        const el = makeEl(dotSvg("#22c55e"), 18);
        const m = new mapboxgl.Marker({
          element: el,
          anchor: "center",
          draggable: interactive && !!onPickupChange,
        })
          .setLngLat([pickup.lng, pickup.lat])
          .addTo(map);
        if (interactive && onPickupChange) {
          m.on("dragend", () => {
            const ll = m.getLngLat();
            onPickupChange({ lat: ll.lat, lng: ll.lng });
          });
        }
        markersRef.current[key] = m;
      }
    } else setMarker("pickup", null);

    setMarker("destination", destination, makeEl(pinSvg("#9333ea"), 34));
    // stops
    Object.keys(markersRef.current).forEach((k) => {
      if (k.startsWith("stop-")) {
        markersRef.current[k].remove();
        delete markersRef.current[k];
      }
    });
    stops.forEach((s, i) => setMarker(`stop-${i}`, s, makeEl(dotSvg("#f59e0b"), 16)));
    carMarker("driver", driver, undefined, driverPulse);

    // live request pins (driver home)
    Object.keys(markersRef.current).forEach((k) => {
      if (k.startsWith("req-")) {
        markersRef.current[k].remove();
        delete markersRef.current[k];
      }
    });
    requestMarkers.forEach((r, i) => setMarker(`req-${i}`, r, makeEl(pinSvg("#9333ea"), 28)));
    carMarker("you", centerOn && requestMarkers.length ? centerOn : (centerOn && !pickup && !destination ? centerOn : null));

    // route + fit
    const pts = [];
    if (pickup) pts.push(pickup);
    stops.forEach((s) => pts.push(s));
    if (destination) pts.push(destination);
    if (driver) pts.push(driver);

    if (trackTo && driver) {
      drawRoute([driver, trackTo]);
    } else if (showRoute && !driverPulse && pickup && destination) {
      drawRoute([pickup, ...stops, destination]);
    } else if ((driverPulse || (!showRoute && !trackTo)) && map.getSource("route")) {
      map.getSource("route").setData({ type: "Feature", geometry: { type: "LineString", coordinates: [] } });
    }

    // Focus tight on the live driver location (rider tracking) instead of fitting the whole route.
    if (driverPulse && driver) {
      map.easeTo({ center: [driver.lng, driver.lat], zoom: 15, duration: 700 });
    } else if (trackTo && driver) {
      const b = new mapboxgl.LngLatBounds();
      [driver, trackTo].forEach((p) => b.extend([p.lng, p.lat]));
      map.fitBounds(b, { padding: 70, maxZoom: 15, duration: 700 });
    } else if (pts.length >= 2) {
      const b = new mapboxgl.LngLatBounds();
      pts.forEach((p) => b.extend([p.lng, p.lat]));
      map.fitBounds(b, { padding: 80, maxZoom: 14, duration: 600 });
    } else if (pts.length === 1) {
      map.easeTo({ center: [pts[0].lng, pts[0].lat], zoom: 13, duration: 600 });
    } else if (centerOn) {
      map.easeTo({ center: [centerOn.lng, centerOn.lat], zoom: 12, duration: 600 });
    }
  }

  return (
    <div className={`relative h-full w-full ${className}`}>
      <div ref={containerRef} className="h-full w-full" />
      {mapErr && (
        <div className="absolute inset-0 flex items-center justify-center bg-secondary p-6 text-center" data-testid="map-error">
          <div className="max-w-xs">
            <p className="text-sm font-semibold text-foreground">Map couldn’t load</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {mapErr === "missing"
                ? "Map isn’t configured for this site."
                : "This domain isn’t authorized for the map yet. Add it to your Mapbox token’s allowed URLs."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
